import React, { useRef } from "react";
import {
  SCALE,
  IMAGE_FIELDS,
  isShapeId,
  snap,
  pdfToCssTop,
  pdfXtoCss,
  textHeightPt,
} from "./constants";
import { devDebug } from "../../lib/debug";

// Named color aliases used in the layout config; anything else falls through
// to raw #hex or the neutral default.
const COLOR_ALIASES = { green: "#1F8A45", orange: "#F58220" };

const resolveColor = (raw) => {
  if (COLOR_ALIASES[raw]) return COLOR_ALIASES[raw];
  if (typeof raw === "string" && raw.startsWith("#")) return raw;
  return "#111";
};

// Label shown inside box-style fields (image placeholders / signature holders).
const BOX_LABELS = {
  table: "TABEL JUMLAH | SATUAN | NAMA BARANG",
  sig_diterima: "DITERIMA OLEH",
  sig_dibuat: "DIBUAT OLEH",
  signature_dibuat: "TTD",
  qr_code: "QR",
  company_logo: "LOGO",
};

const boxLabel = (id) => BOX_LABELS[id] || "PRODUK";

/**
 * Pick the correct on-canvas rendered content depending on the field kind.
 * Shape → blank; box → static label; text → sample/text.
 */
function renderFieldContent({ isShape, isBox, id, displayText }) {
  if (isShape) return "";
  if (isBox) return boxLabel(id);
  return displayText;
}

/**
 * Draggable field on the layout canvas. Renders as either:
 *  - text label (line-height single)
 *  - image/box placeholder (dashed border, centered caption)
 *  - shape rectangle / line (stroke + fill)
 *
 * All position math (PDF→CSS) happens here so the parent stays clean.
 */
export default function FieldElement({
  id,
  cfg,
  selected,
  onSelect,
  onDrag,
  onDragStart,
  onDragEnd,
  sample,
  gridStep,
  pageH,
}) {
  const ref = useRef(null);
  const drag = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  const isCustom = id.startsWith("custom_");
  const isShape = isShapeId(id);
  const isLine = isShape && cfg.type === "line";
  const isBox =
    ["table", "sig_diterima", "sig_dibuat", "signature_dibuat", "company_logo", "product_logo", "qr_code"].includes(id) ||
    (isShape && !isLine);

  // Compute CSS box
  let leftPx;
  let topPx;
  let widthPx;
  let heightPx;
  if (isLine) {
    const w = Math.abs(cfg.w || 100);
    const h = Math.abs(cfg.h || 0);
    // For line: PDF coords (x,y) is start point. Render a thin rectangle from start.
    leftPx = pdfXtoCss(cfg.x);
    // y is start y; line goes to y+h. Use max y for top calc.
    const topY = Math.max(cfg.y, cfg.y + (cfg.h || 0));
    topPx = pdfToCssTop(topY, 0, pageH);
    widthPx = Math.max(w, 4) * SCALE;
    heightPx = Math.max(h, 2) * SCALE;
  } else if (isBox) {
    const w = cfg.w || 60;
    const h = cfg.h || 60;
    leftPx = pdfXtoCss(cfg.x);
    topPx = pdfToCssTop(cfg.y, h, pageH);
    widthPx = w * SCALE;
    heightPx = h * SCALE;
  } else {
    const th = textHeightPt(cfg);
    leftPx = pdfXtoCss(cfg.x);
    topPx = pdfToCssTop(cfg.y, th, pageH);
    widthPx = null;
    heightPx = null;
  }

  const handlePointerDown = (e) => {
    e.stopPropagation();
    onSelect(id);
    drag.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: cfg.x,
      origY: cfg.y,
    };
    if (onDragStart) onDragStart();
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!drag.current.active) return;
    const dx = (e.clientX - drag.current.startX) / SCALE;
    const dy = (e.clientY - drag.current.startY) / SCALE;
    const rawX = drag.current.origX + dx;
    const rawY = drag.current.origY - dy;
    const step = gridStep || 0;
    onDrag(id, { x: snap(rawX, step), y: snap(rawY, step) });
  };

  const handlePointerUp = (e) => {
    if (drag.current.active) {
      drag.current.active = false;
      if (onDragEnd) onDragEnd();
    }
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      devDebug("releasePointerCapture failed (safe)", err);
    }
  };

  // Font family CSS mapping
  const cssFontFamily = {
    helvetica: "Helvetica, Arial, sans-serif",
    times: "'Times New Roman', Times, serif",
    courier: "'Courier New', Courier, monospace",
  }[cfg.font || "helvetica"];

  // Color CSS
  const colorCss = resolveColor(cfg.color);

  const style = {
    position: "absolute",
    left: leftPx,
    top: topPx,
    minWidth: 20,
    minHeight: 12,
    padding: "1px 3px",
    cursor: "move",
    userSelect: "none",
    border: selected
      ? "1.5px dashed #F58220"
      : isCustom
      ? "1px dashed rgba(31,138,69,0.6)"
      : "1px dashed transparent",
    background: selected
      ? "rgba(245,130,32,0.08)"
      : isCustom
      ? "rgba(31,138,69,0.05)"
      : "transparent",
    fontFamily: cssFontFamily,
    fontSize: `${(cfg.size || 8) * SCALE}px`,
    fontWeight: cfg.bold ? 700 : 400,
    fontStyle: cfg.italic ? "italic" : "normal",
    textDecoration: cfg.underline ? "underline" : "none",
    color: colorCss,
    lineHeight: 1,
    whiteSpace: "nowrap",
    transformOrigin: "top left",
  };

  if (isBox) {
    Object.assign(style, {
      width: widthPx,
      height: heightPx,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: selected ? "rgba(245,130,32,0.15)" : "rgba(0,0,0,0.03)",
      border: selected ? "1.5px dashed #F58220" : "1px dashed rgba(0,0,0,0.35)",
      fontSize: `${Math.min(10, heightPx / 6)}px`,
      color: "#666",
    });
  }

  // Override for shape rectangles: render with actual stroke/fill
  if (isShape && !isLine) {
    const stroke = cfg.stroke_color || "#111";
    const strokeW = Math.max(1, (cfg.stroke_width || 0.8) * SCALE);
    const fill =
      cfg.fill_color && cfg.fill_color !== "none" && String(cfg.fill_color).trim()
        ? cfg.fill_color
        : "transparent";
    Object.assign(style, {
      width: widthPx,
      height: heightPx,
      background: fill,
      border: `${strokeW}px solid ${stroke}`,
      borderRadius: (cfg.radius || 0) * SCALE,
      color: "transparent",
      outline: selected ? "1.5px dashed #F58220" : "none",
      outlineOffset: "2px",
    });
  }

  // Override for line shapes
  if (isLine) {
    const stroke = cfg.stroke_color || "#111";
    const strokeW = Math.max(1, (cfg.stroke_width || 0.8) * SCALE);
    Object.assign(style, {
      width: widthPx,
      height: strokeW,
      background: stroke,
      border: "none",
      color: "transparent",
      outline: selected ? "1.5px dashed #F58220" : "none",
      outlineOffset: "3px",
    });
  }

  if (cfg.hidden) style.opacity = 0.25;

  const displayText = cfg.text || sample || id;

  return (
    <div
      ref={ref}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={style}
      data-testid={`layout-field-${id}`}
      data-field-id={id}
    >
      {renderFieldContent({ isShape, isBox, id, displayText })}
    </div>
  );
}

// Re-export IMAGE_FIELDS for parent convenience so consumers that need it
// don't have to know about the constants module structure.
export { IMAGE_FIELDS };
